/**
 * Sea-state mapping for the ocean surface.
 *
 * A single 0..1 storm value (from the server-authoritative weather) is eased
 * into nine visual parameters:
 *
 * - `amplitude`  visual Gerstner height scale (CPU buoyancy is wired separately
 *                by the integrator so both paths stay on the same wave field)
 * - `foam`       crest foam strength tinted into the water shader
 * - `spray`      hull/wake spray amount fed to `ShipWake`
 * - `whitecap`   bright whitecap strength on the steepest crests
 * - `windStreak` wind-aligned specular streak/glitter strength
 * - `chop`       fine high-frequency normal detail (damped in calm water)
 * - `skyReflection` weight of the reflected sky vs. the calm subsurface tint
 * - `subsurface`   strength of the translucent crest/trough scatter, 0..1
 * - `storm`      eased storm intensity used for the cold specular tint and the
 *                trough-darkening term
 *
 * Everything here is pure arithmetic — no `Math.random`, no allocation per
 * frame beyond the config object replaced on `setStorm`.
 */

export interface SeaStateConfig {
  /** Visual wave-height scale applied to the shared Gerstner field. */
  amplitude: number;
  /** Crest foam strength on the water surface, 0..1. */
  foam: number;
  /** Hull/wake spray amount, 0..1. */
  spray: number;
  /** Bright whitecap strength on the steepest crests, 0..1. */
  whitecap: number;
  /** Wind-aligned specular streak/glitter strength, 0..1. */
  windStreak: number;
  /** Fine chop amplitude layered onto the surface normal, 0..1. */
  chop: number;
  /** Sky-reflection weighting: higher leans on the reflected sky, 0..1. */
  skyReflection: number;
  /** Translucent subsurface scatter strength on crests/troughs, 0..1. */
  subsurface: number;
  /** Eased storm intensity, 0..1 (colder specular + darker troughs). */
  storm: number;
}

/** Calm sea: baseline wave height, no foam, spray, whitecaps or streaks. */
export const SEA_STATE_CALM: Readonly<SeaStateConfig> = {
  amplitude: 1,
  foam: 0,
  spray: 0,
  whitecap: 0,
  windStreak: 0,
  chop: 0.35,
  skyReflection: 0.55,
  subsurface: 0.4,
  storm: 0,
};

/** Storm sea: heavier swell with full crest foam, whitecaps, spray and glitter. */
export const SEA_STATE_STORM: Readonly<SeaStateConfig> = {
  amplitude: 1.85,
  foam: 1,
  spray: 1,
  whitecap: 1,
  windStreak: 1,
  chop: 1,
  skyReflection: 1,
  subsurface: 0.9,
  storm: 1,
};

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/** Smoothstep easing: gentle build-up, then a confident rise to full storm. */
function easeStorm(storm: number): number {
  return storm * storm * (3 - 2 * storm);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export class SeaState {
  private _config: SeaStateConfig = { ...SEA_STATE_CALM };

  /** Current eased configuration. */
  get config(): SeaStateConfig {
    return this._config;
  }

  /** Map a 0..1 storm intensity to an eased `SeaStateConfig`. */
  setStorm(storm: number): void {
    const eased = easeStorm(clamp01(storm));
    this._config = {
      amplitude: lerp(SEA_STATE_CALM.amplitude, SEA_STATE_STORM.amplitude, eased),
      foam: lerp(SEA_STATE_CALM.foam, SEA_STATE_STORM.foam, eased),
      spray: lerp(SEA_STATE_CALM.spray, SEA_STATE_STORM.spray, eased),
      whitecap: lerp(SEA_STATE_CALM.whitecap, SEA_STATE_STORM.whitecap, eased),
      windStreak: lerp(SEA_STATE_CALM.windStreak, SEA_STATE_STORM.windStreak, eased),
      chop: lerp(SEA_STATE_CALM.chop, SEA_STATE_STORM.chop, eased),
      skyReflection: lerp(SEA_STATE_CALM.skyReflection, SEA_STATE_STORM.skyReflection, eased),
      subsurface: lerp(SEA_STATE_CALM.subsurface, SEA_STATE_STORM.subsurface, eased),
      storm: eased,
    };
  }
}
